/**
 * Chat Context Store
 *
 * Zustand store tracking what page/entity the user is on for the global AI chat panel.
 * Manages ambient context (current page) and explicit entity context (selected documents, etc.).
 */

import { create } from "zustand";

export type PageContext =
  | "documents"
  | "audits"
  | "checklists"
  | "settings"
  | "home";

export interface AmbientContext {
  page: PageContext;
  label: string;
}

export interface EntityMetadata {
  status?: string;
  completionPercent?: number;
  deadline?: number;
  standardType?: string;
  parentAuditId?: string;
  processingStatus?: string;
}

export interface EntityContext {
  type: "document" | "audit" | "checklist-item" | "clause";
  id: string;
  label: string;
  metadata?: EntityMetadata;
}

interface ChatContextState {
  ambient: AmbientContext | null;
  entities: EntityContext[];

  // Actions
  setAmbientContext: (ambient: AmbientContext) => void;
  clearAmbientContext: () => void;
  addEntity: (entity: EntityContext) => void;
  removeEntity: (id: string) => void;
  clearEntities: () => void;
}

export const useChatContext = create<ChatContextState>((set) => ({
  ambient: null,
  entities: [],

  setAmbientContext: (ambient) => set({ ambient }),
  clearAmbientContext: () => set({ ambient: null }),

  addEntity: (entity) =>
    set((state) => {
      if (state.entities.some((e) => e.id === entity.id)) {
        return state;
      }
      return { entities: [...state.entities, entity] };
    }),

  removeEntity: (id) =>
    set((state) => ({
      entities: state.entities.filter((e) => e.id !== id),
    })),

  clearEntities: () => set({ entities: [] }),
}));

/** Selector: document IDs from entity context */
export const useDocumentIds = () =>
  useChatContext((s) =>
    s.entities.filter((e) => e.type === "document").map((e) => e.id),
  );

/** Serialize context for backend API calls */
export const useChatContextForApi = () =>
  useChatContext((s) => {
    if (!s.ambient && s.entities.length === 0) return undefined;

    const entityLabels = s.entities.map(
      (e) => `${e.type} "${e.label}" (id: ${e.id})`,
    );

    return {
      page: s.ambient?.page,
      pageLabel: s.ambient?.label,
      entitySummary:
        entityLabels.length > 0
          ? `The user is looking at: ${entityLabels.join(", ")}`
          : undefined,
    };
  });

/** Selector: structured context with entity metadata for enriched API calls */
export function useChatContextStructured() {
  const ambient = useChatContext((s) => s.ambient);
  const entities = useChatContext((s) => s.entities);

  if (!ambient && entities.length === 0) return undefined;

  return {
    page: ambient?.page,
    pageLabel: ambient?.label,
    entities: entities.map((e) => ({
      type: e.type,
      id: e.id,
      label: e.label,
      metadata: e.metadata,
    })),
  };
}
